'use client';

import { ReactNode, useState } from 'react';
import { useTranslations } from 'next-intl';
import { buildRoute, CacheKeys, ServerActionName } from '@headless-workspace/config';
import { useToasts } from '@headless-workspace/core-ui';
import { RichText, useSafeSWRMutation } from '@headless-workspace/domain/common/client';
import { BooleanFormField, UserSephoraCardType } from '@headless-workspace/domain/common/server';
import {
    ComposableContent,
    GlowButton,
    GlowCheckbox,
    GlowDivider,
    GlowTextCaption,
    PropsWithLocale,
} from '@headless-workspace/glow-ds';
import { Component } from '@headless-workspace/typings';
import { ReviewPayload, sendRatingsAndReviewsServiceServerAction } from '../../../../server';
import { ReviewCard } from './ReviewCard';

type RatingsAndReviewsModalProps = PropsWithLocale & {
    gender?: string;
    acceptedTermsAndConditions: BooleanFormField;
    reviewPayload?: ReviewPayload;
    productId: string;
    age?: string;
    userCard?: UserSephoraCardType;
    onConfirm: () => void;
    ProductInfos: ReactNode;
};

export const RatingsAndReviewsFormInformation: Component<RatingsAndReviewsModalProps> = ({
    age,
    reviewPayload,
    gender,
    userCard,
    acceptedTermsAndConditions,
    productId,
    locale,
    onConfirm,
    ProductInfos,
}) => {
    const t = useTranslations('Discover.Pdp.ratingAndReviewsForm');

    const [acceptedCGUChecked, setAcceptedCGUChecked] = useState(false);
    const [errorText, setErrorText] = useState<string | undefined>();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const { notifySuccess, notifyError } = useToasts();

    const { trigger: postReview } = useSafeSWRMutation(
        CacheKeys.RatingReviewForm,
        ServerActionName.postRatingReviewForm,
        async () => {
            return await sendRatingsAndReviewsServiceServerAction(productId, reviewPayload);
        },
    );

    if (!reviewPayload) return null;

    const handleSubmit = async () => {
        setIsLoading(true);

        if (!acceptedCGUChecked && acceptedTermsAndConditions.isRequired) {
            setErrorText(t('error.mandatoryField'));
            setIsLoading(false);
            return;
        }

        setErrorText(undefined);
        reviewPayload.acceptedTermsAndConditions = true;

        const result = await postReview();
        if (result.type === 'success') {
            notifySuccess(t('label.successSubmitReview'));
        } else if (result.type === 'failure') {
            notifyError(result.meta.message);
        }
        setIsLoading(false);
        onConfirm();
    };

    const genderText = gender ? t('label.gender', { gender }) : undefined;

    return (
        <div className={'flex flex-1 flex-col'}>
            <div className={'flex flex-col gap-1 overflow-auto h-full relative'}>
                <div className={'absolute flex flex-col gap-1 p-1'}>
                    {ProductInfos}
                    <ReviewCard
                        rating={reviewPayload.rating}
                        createdAt={new Date()}
                        userName={reviewPayload.nickName}
                        userAge={age}
                        userGender={genderText}
                        userCard={userCard}
                        title={reviewPayload.title}
                        content={reviewPayload.content}
                        isRecommended={reviewPayload.isRecommended}
                        locale={locale}
                        translatable={false}
                        truncateContent={false}
                        TagName={'div'}
                    />

                    <div className={'flex flex-col'}>
                        <GlowTextCaption text={t('label.legalMentions')} color={'tertiary'} />
                        <ComposableContent.Accordion
                            accordion={{
                                title: (
                                    <GlowTextCaption
                                        textDecoration={'underline'}
                                        text={t('label.legalMentionsTitle')}
                                    />
                                ),
                                content: (
                                    <RichText
                                        messageKey={'Discover.Pdp.ratingAndReviewsForm.label.legalMentionsSeeMore'}
                                        fontSize={'caption'}
                                        innerTextClassname={'!inline'}
                                        links={{
                                            privacyPolicyUrl: { href: buildRoute(locale, 'privacyPolicy') },
                                        }}
                                    />
                                ),
                            }}
                        />
                    </div>
                </div>
            </div>

            <div className={'w-full bg-background-l0'}>
                <GlowDivider />

                <div className={'flex p-0.75 flex-col gap-0.5'}>
                    <GlowCheckbox
                        id={acceptedTermsAndConditions.id}
                        name={acceptedTermsAndConditions.id}
                        isRequired={acceptedTermsAndConditions.isRequired}
                        type={errorText ? 'error' : 'default'}
                        className={'flex flex-row'}
                        warningText={errorText}
                        labelElement={
                            <RichText
                                messageKey={'Discover.Pdp.ratingAndReviewsForm.label.agreedToTermsAndCondition'}
                                fontSize={'body'}
                                links={{
                                    reviewConditionsUrl: { href: buildRoute(locale, 'reviewConditions') },
                                }}
                            />
                        }
                        checked={acceptedCGUChecked}
                        onChange={() => setAcceptedCGUChecked(!acceptedCGUChecked)}
                    />
                    <GlowButton
                        className={'flex-1 w-full'}
                        label={t('action.sendReview')}
                        variant={'primary'}
                        onClick={handleSubmit}
                        isSpinnerActive={isLoading}
                    />
                </div>
            </div>
        </div>
    );
};
